/**
 * Cookie utilities for the Grit Mile landing page.
 *
 * gritmile_vid — visitor ID (UUID, 30-day max-age, SameSite=Lax).
 *   Used to assign a deterministic A/B variant and persist it across reloads.
 *
 * gritmile_sid — session ID (UUID, no max-age = session cookie).
 *   Used to deduplicate impression telemetry within a browser session.
 */

import type { VariantId } from "./variants.js";

// ── Cookie names ──────────────────────────────────────────────────────────────

export const VISITOR_COOKIE = "gritmile_vid";
export const SESSION_COOKIE = "gritmile_sid";

// ── Internal helpers ──────────────────────────────────────────────────────────

/** Read a cookie value by name from document.cookie. Returns null if absent. */
export function getCookieValue(name: string): string | null {
  const prefix = `${name}=`;
  const cookies = document.cookie.split(";");
  for (const raw of cookies) {
    const cookie = raw.trim();
    if (cookie.startsWith(prefix)) {
      return cookie.slice(prefix.length);
    }
  }
  return null;
}

// ── Visitor cookie ─────────────────────────────────────────────────────────────

/**
 * Return the existing visitor UUID (gritmile_vid), or generate and persist one.
 * 30-day max-age, SameSite=Lax, path=/.
 */
export function getOrCreateVisitorCookie(): string {
  const existing = getCookieValue(VISITOR_COOKIE);
  if (existing !== null) return existing;

  const id = crypto.randomUUID();
  const maxAge = 30 * 24 * 60 * 60; // 30 days in seconds
  document.cookie = `${VISITOR_COOKIE}=${id}; max-age=${maxAge}; SameSite=Lax; path=/`;
  return id;
}

// ── Session cookie ─────────────────────────────────────────────────────────────

/**
 * Return the existing session UUID (gritmile_sid), or generate and persist one.
 * No max-age → cleared when browser tab closes (session cookie).
 */
export function getOrCreateSessionCookie(): string {
  const existing = getCookieValue(SESSION_COOKIE);
  if (existing !== null) return existing;

  const id = crypto.randomUUID();
  document.cookie = `${SESSION_COOKIE}=${id}; SameSite=Lax; path=/`;
  return id;
}

// ── Variant assignment ─────────────────────────────────────────────────────────

/**
 * Deterministically map a visitor UUID to a variant index (0–8).
 *
 * Algorithm: sum of UTF-16 char codes modulo 9. This is stable across calls
 * (same UUID → same index) and distributes uniformly over the 9 buckets.
 */
export function getVariantIndex(visitorId: string): VariantId {
  let sum = 0;
  for (let i = 0; i < visitorId.length; i++) {
    sum += visitorId.charCodeAt(i);
  }
  return (sum % 9) as VariantId;
}
